import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { BehaviorSubject, delay, Observable, of } from "rxjs";
import {
  SyncHpcConfig,
  UpdateGroupPolicyProp,
} from "../models/config-entities";
import { EnvironmentService } from "./environment.service";
import { ServiceBroadcast } from "../models/serviceBroadcast";
import { 
  CreateImageRequest, 
  CreateImageWithRepositoryRequest, 
  CreateRegistryRequest,
  CreateRepositoryRequest,
  ImageDetailedInfoResponse,
  RegistryOperationsRequest,
  RepositoryDetail,
  UntagImageRequest
} from "../models/repository";

@Injectable({
  providedIn: "root",
})
export class ConfigEntityService {
  public SyncHpcXMLParameters: any = [];

  checkIfLoginEnabled() {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "SyncConfiguration/SyncHPC/CheckLogin";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.get(baseUrl, { headers: headers });
  }

  constructor(private http: HttpClient, private env: EnvironmentService) {}

  private activeEntity = new BehaviorSubject<string>("Overview");
  public ActiveEntity = this.activeEntity.asObservable();

  sendActiveEntity(name: string) {
    this.activeEntity.next(name);
  }

  getAllConfigEntities() {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "SyncEntities";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.get(baseUrl, { headers: headers });
  }

  getAllSyncXmlEntities() {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "SyncEntities/SyncXML";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.get(baseUrl, { headers: headers });
  }

  getAllWebConfigEntities() {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "SyncEntities/WebConfig";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.get(baseUrl, { headers: headers });
  }

  SetWebConfigEntityValue(typeName: string, paramName: string, value: string) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "SyncEntities/" +
      typeName +
      "/" +
      paramName +
      "/" +
      value;
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.get(baseUrl, { headers: headers });
  }

  updateWebConfigEntityValue(
    typeName: string,
    paramName: string,
    value: string
  ) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "SyncEntities/" +
      typeName;
    let headers = new HttpHeaders();
    let body = { key: paramName, value: value };
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.put(baseUrl, body, { headers: headers });
  }

  getStorageConfig(depName: string, storageName: string) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "SyncConfiguration/Storage/" +
      storageName +
      "/GetConfiguration";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache")
      .set("depName", depName);
    return this.http.get(baseUrl, { headers: headers });
  }

  getDeploymentConfigs(depName: string, cloudType: string) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "SyncConfiguration/Deployment/" +
      depName +
      "/GetConfiguration";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache")
      .set("CloudType", cloudType);
    return this.http.get(baseUrl, { headers: headers });
  }

  saveDeploymentConfiguration(
    depName: string,
    cloudType: string,
    configureData: SyncHpcConfig
  ) {
    try {
      let baseUrl =
        this.env.envVariables.httpProtocol +
        this.env.envVariables.webHost +
        "SyncConfiguration/Deployment/" +
        depName +
        "/SetConfiguration";
      let headers = new HttpHeaders();
      headers = headers
        .set("Content-Type", "application/json; charset=utf-8")
        .set("Cache-Control", "no-cache")
        .set("CloudType", cloudType);

      let options = { headers: headers };
      return this.http.post<any>(baseUrl, configureData, options);
    } catch (Error) {}
  }

  updateStorageConfig(
    depName: string,
    storageName: string,
    storageConfig: SyncHpcConfig
  ) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "SyncConfiguration/Storage/" +
      storageName +
      "/SetConfiguration";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache")
      .set("depName", depName);
    return this.http.put<any>(baseUrl, storageConfig, { headers: headers });
  }

  getSyncHPCConfig() {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "SyncConfiguration/SyncHPC/GetConfiguration";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.get(baseUrl, { headers: headers });
  }

  updateSyncHPCConfig(syncHpcConfig: SyncHpcConfig) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "SyncConfiguration/SyncHPC/SetConfiguration";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.put<any>(baseUrl, syncHpcConfig, { headers: headers });
  }

  downloadSyncHPCLogs() {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "SyncHPCInfoes/GetSyncHPCLogFile";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "text/plain; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.get(baseUrl, { headers: headers, responseType: "blob" });
  }

  getNodeConfigAll() {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "SyncConfiguration/Node/GetConfiguration";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.get(baseUrl, { headers: headers });
  }

  getHAStatus() {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "SyncConfiguration/HA/GetHAStatus";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.get(baseUrl, { headers: headers });
  }

  addServiceBroadcast(serviceBroadcast: ServiceBroadcast) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "SyncConfiguration/SyncHPC/SetServiceBroadcast";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.put<any>(baseUrl, serviceBroadcast, { headers: headers });
  }

  getServiceBroadcasts() {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "SyncConfiguration/SyncHPC/GetServiceBroadcast";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.get(baseUrl, { headers: headers });
  }

  updateServiceBroadcast(serviceBroadcast: ServiceBroadcast) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "SyncConfiguration/SyncHPC/UpdateServiceBroadcast";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.put<any>(baseUrl, serviceBroadcast, { headers: headers });
  }

  getAllInfraCredentials() {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "SyncConfiguration/SyncHPC/GetAllCredentials";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.get<any>(baseUrl, { headers: headers });
  }

  addNewInfraCred(infraCred, name) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "SyncConfiguration/SyncHPC/SetCredential";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.post(baseUrl, infraCred, { headers: headers });
  }

  deleteInfraCred(name) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "SyncConfiguration/SyncHPC/DeleteCredential/" +
      name;
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.delete(baseUrl, { headers: headers });
  }

  updateInfraCred(name, infraCred, depName = "") {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "SyncConfiguration/SyncHPC/UpdateCredential/" +
      name;
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache")
      .set("DeploymentName", depName);
    return this.http.post(baseUrl, infraCred, { headers: headers });
  }

  getCredForUpdate(name, depName = "") {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "SyncConfiguration/SyncHPC/GetCredential/" +
      name;
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache")
      .set("DeploymentName", depName);
    return this.http.get(baseUrl, { headers: headers });
  }

  getInfraCredentialJson(infra) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "SyncConfiguration/SyncHPC/GetInfraCredentialJson/" +
      infra;
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.get(baseUrl, { headers: headers });
  }

  getAllImagesList() {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "SyncConfiguration/SyncHPC/GetImageDetails";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.get<any>(baseUrl, { headers: headers });
  }

  addNewImageDetails(fileImage) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "SyncConfiguration/SyncHPC/AddImageDetails";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.post(baseUrl, fileImage, { headers: headers });
  }

  updateImageDetails(fileImage, friendlyName) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "SyncConfiguration/SyncHPC/UpdateImageDetails/" +
      friendlyName;
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.post(baseUrl, fileImage, { headers: headers });
  }

  deleteImageDetails(friendlyName) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "SyncConfiguration/SyncHPC/DeleteImage/" +
      friendlyName;
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.delete(baseUrl, { headers: headers });
  }

  getEntitySettings() {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "SyncConfiguration/SyncHPC/GetEntitySetting";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.get<any>(baseUrl, { headers: headers });
  }

  saveEntitySettings(entities: string): Observable<any> {
    let entityObj = {
      entity: entities,
    };
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "SyncConfiguration/SyncHPC/SetEntitySettings";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.post<any>(baseUrl, entityObj, { headers: headers });
  }

  addGroupPolicyObject(gpo) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "SyncConfiguration/SyncHPC/AddGpo";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.post(baseUrl, gpo, { headers: headers });
  }

  getGroupPolicyObjects() {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "SyncConfiguration/SyncHPC/GetGPOs";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.get(baseUrl, { headers: headers });
  }

  updateGroupPolicy(updateGpoObj: UpdateGroupPolicyProp) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "SyncConfiguration/SyncHPC/UpdateGPO";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.post(baseUrl, updateGpoObj, { headers: headers });
  }

  getGpoPropValue(gpoName: string) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "SyncConfiguration/SyncHPC/GetRegistryKeyNameValues/" +
      gpoName;
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.get(baseUrl, { headers: headers });
  }

  getGpoWithPropValue() {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "SyncConfiguration/SyncHPC/GetGpoPropValues";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.get(baseUrl, { headers: headers });
  }

  getSyncHPCLicenseInfoService() {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "SyncConfiguration/SyncHPC/SyncHPCLicenseInfo";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.get<any>(baseUrl, { headers: headers });
  }

  getGiteaConfig() {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "SyncConfiguration/SyncHPC/GetGiteaConfig";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.get(baseUrl, { headers: headers });
  }

  createRegistry(request: CreateRegistryRequest) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "SyncConfiguration/SyncHPC/CreateRegistry";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.post(baseUrl, request, { headers: headers });
  }

  listRegistries(){
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "SyncConfiguration/SyncHPC/GetRegistries";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.get<any>(baseUrl, { headers: headers });
  }

  registryOperation(request: RegistryOperationsRequest) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "SyncConfiguration/SyncHPC/RegistryOperations";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.post(baseUrl, request, { headers: headers });
  }
  
  createRepository(request: CreateRepositoryRequest) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "SyncConfiguration/SyncHPC/CreateRepository";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.post(baseUrl, request, { headers: headers });
  }

  listRepositories() {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "SyncConfiguration/SyncHPC/GetRepositories";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.get<any>(baseUrl, { headers: headers });
  }

  getAllRepositories(): Observable<RepositoryDetail[]> {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "SyncConfiguration/SyncHPC/GetRepositories";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.get<any>(baseUrl, { headers: headers });
  }

  createImage(request: CreateImageRequest){
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "SyncConfiguration/SyncHPC/CreateLocalImage";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.post(baseUrl, request, { headers: headers });
  }

  createImageWithRepository(request: CreateImageWithRepositoryRequest) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "SyncConfiguration/SyncHPC/CreateLocalImageWithAutoPush";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.post(baseUrl, request, { headers: headers });
  }

  listLocalImages(filter?: string) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "SyncConfiguration/SyncHPC/GetLocalImages";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.get<any>(baseUrl, { headers: headers });
  }

  getImageInfo(imageName: string): Observable<ImageDetailedInfoResponse> {
    const response: ImageDetailedInfoResponse = {
      image_name: imageName,
      id: 'sha256:' + Math.random().toString(36).substring(2, 15),
      created: new Date().toISOString(),
      size: Math.floor(Math.random() * 200000000 + 50000000),
      architecture: 'amd64',
      os: 'linux',
      config: {
        env: ['PATH=/usr/local/sbin:/usr/local/bin:/usr/sbin:/usr/bin:/sbin:/bin'],
        cmd: ['/bin/sh'],
        entrypoint: [],
        exposed_ports: ['80/tcp', '443/tcp'],
        labels: {
          'maintainer': 'Docker Registry Manager',
          'version': '1.0.0'
        },
        user: 'root',
        working_dir: '/'
      },
      layers: [
        {
          id: 'sha256:layer1',
          created: new Date().toISOString(),
          created_by: '/bin/sh -c #(nop) ADD file:abc123 in /',
          size: 5600000,
          comment: 'Base layer'
        },
        {
          id: 'sha256:layer2',
          created: new Date().toISOString(),
          created_by: '/bin/sh -c apk add --no-cache curl',
          size: 2400000,
          comment: 'Install curl'
        }
      ],
      layer_count: 2,
      root_fs: {
        type: 'layers',
        layers: ['sha256:layer1', 'sha256:layer2']
      },
      timestamp: new Date().toISOString()
    };

    return of(response).pipe(delay(1000));
  }

  untagImage(request: UntagImageRequest) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "SyncConfiguration/SyncHPC/UntagImage";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.post(baseUrl, request, { headers: headers });
  }
}
